<script setup lang="ts">
    // @ts-expect-error raison: lib externe mal typée
    import { defineProps, defineEmits, reactive, ref, onMounted, computed, inject, Ref, watch } from 'vue';
    import CustomButton from '@/components/CustomButton.vue';
    import { useConfirmationStore } from '@/stores/confirmation.store';
    import axios from 'axios';
    import { useToastStore } from '@/stores/toast.store';
    import { useRoute } from 'vue-router';
import moment from 'moment';

    const route = useRoute();
    const confirmationStore = useConfirmationStore();
    const toastStore = useToastStore();
    const emits = defineEmits(['updated']);
    const itemsList = ref([] as any[]);
    const filteredDatas = ref([] as Array<any>);
    const paginatedFilteredDatas = ref([] as Array<any>);
    const queryParams = reactive({
        pageNumber: 1
    });
    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1
    });
    const filtersParams = reactive({
        name: '',
        num_lot: '',
        borneInf: null,
        borneSup: null,
        etatAssignation: ''
    });
    const startupInfos = reactive({
        lots: [] as any[],
    });
    const loading = ref(false);
    const loadingActions = ref(false);
    const collectivite = inject<Ref<any>>('collectivite');
    const selectedItems = ref([] as any[]);
    const areAllItemsSelected = computed(() => {
        return paginatedFilteredDatas.value.length > 0 && selectedItems.value.filter(i => paginatedFilteredDatas.value.findIndex(p => p.id === i) !== -1).length === paginatedFilteredDatas.value.length;
    });
    const areAllItemsSelectedAssignable = computed(() => {
        return selectedItems.value.length > 0 && countItemsSelectedAssignable.value === selectedItems.value.length;
    });
    const countItemsSelectedAssignable = computed(() => {
        return itemsList.value.filter(i => selectedItems.value.includes(i.user_id) && !i.carte_id).length;
    });
    const showAdvancedFilters = ref(false);

    onMounted(() => {
        doListCarteItems();
    });

    watch(filtersParams, (oldValues, newValues) => {
        applyFilters();
        onPaginate(1);
    });

    function handleCheckAll(_event: any) {
        const checked = _event.target.checked;
        if (checked) {
            selectedItems.value = [...selectedItems.value.filter(s => itemsList.value.findIndex(p => p.id === s) === -1), ...itemsList.value.map(i => i.id)];
        } else {
            selectedItems.value = [];
        }
    }

    async function doListCarteItems(withoutLoading?: boolean) {
        try {
            if (!withoutLoading) { loading.value = true; }
            const httpResponse = await axios.get(`/backoffice/collectivites/${route.params.id}/get_collectivite_cartes_datas`);
            itemsList.value = httpResponse.data.data;
            startupInfos.lots = [...new Set(itemsList.value.filter(a => a.num_lot_carte).map(a => a.num_lot_carte))];
            applyFilters();
            onPaginate(1);
        } catch (error: any) {
            toastStore.error(error.message ?? "Une erreur est survenue lors de la requête au serveur");
        } finally {
            loading.value = false;
        }
    }

    function onPaginate(page: number) {
        queryParams.pageNumber = page;
        pagination.total = filteredDatas.value.length;
        pagination.last_page = Math.ceil(pagination.total / pagination.per_page);
        const start = (page - 1) * pagination.per_page;
        paginatedFilteredDatas.value =  filteredDatas.value.slice(start, start + pagination.per_page);
    }

    async function assignCartes(usersIds: any[]) {
        const confirmation = await confirmationStore.open('Assignation Carte', `Êtes-vous sûre de vouloir assigner ${usersIds.length > 1 ? `des cartes aux ${usersIds.length} membres sélectionnés` : `une carte au membre sélectionné`} ?`);
        if (!confirmation) return;

        try {
            loadingActions.value = true;
            await axios.post(`/backoffice/collectivites/${collectivite?.value.id}/assignation_cartes`, { ids: usersIds });
            selectedItems.value = [];
            doListCarteItems(true);
            toastStore.success("Assignation(s) effectuée(s)");
        } catch(error: any) {
            toastStore.error(error.message || 'Une erreur est survenue lors de la requête au serveur.');
        } finally {
            loadingActions.value = false;
        }
    }

    function applyFilters() {
        let allDatas = itemsList.value;

        if (filtersParams.name) {
            allDatas = allDatas.filter(a => `${a.nom_user} ${a.prenom_user}`.toLowerCase().includes(filtersParams.name.toLowerCase()));
        }
        if (filtersParams.num_lot) {
            allDatas = allDatas.filter(a => a.num_lot_carte == filtersParams.num_lot);
        }
        if (filtersParams.borneInf) {
            const borneInf = moment(filtersParams.borneInf);
            if (borneInf.isValid()) {
                allDatas = allDatas.filter(a => {
                    const date_creation_carte = moment(a.created_at_carte);
                    return date_creation_carte.isValid() && date_creation_carte.isSameOrAfter(borneInf);
                })
            }
        }
        if (filtersParams.borneSup) {
            const borneSup = moment(filtersParams.borneSup);
            if (borneSup.isValid()) {
                allDatas = allDatas.filter(a => {
                    const date_creation_carte = moment(a.date_creation_carte);
                    return date_creation_carte.isValid() && date_creation_carte.isSameOrBefore(borneSup);
                })
            }
        }
        if (filtersParams.etatAssignation) {
            allDatas = allDatas.filter(a => filtersParams.etatAssignation == '1' && a.carte_id || filtersParams.etatAssignation == '2' && !a.carte_id);
        }
        
        filteredDatas.value = allDatas;
    }

    function toggleAdvancedFilters() {
        showAdvancedFilters.value = !showAdvancedFilters.value;
    }
</script>
<template>
    <section class="s20_mobile px-4">
        <div class="widget px-7 py-7">
            <div class="s7_mobile container-full-2"> 
                <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                    <img src="/icons/icon-info-perso.svg" class="size-l" alt="" />
                    Cartes
                </h2>
            </div> 
            <div class="s13 flex justify-content-right">
                <template v-if="areAllItemsSelectedAssignable">
                    <custom-button type="button" :classesList="'btn-ultra color2'" @click="assignCartes(selectedItems)" :loading="loadingActions">
                        <span>Assigner carte ({{ countItemsSelectedAssignable }})</span>
                    </custom-button>
                </template>
                <custom-button type="button" :classesList="'btn light'" @click="doListCarteItems">
                    <img src="/icons/icon-list.svg" class="btn-icon" alt="" />
                    <span>Actualiser</span>
                </custom-button>
                <button class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter" @click="toggleAdvancedFilters">
                    <img src="/icons/icon-filter.svg" class="btn-icon" alt="" />
                    <span>Options</span>
                </button>
            </div>
            <div class="clear-both"></div>

            <div class="s20 items-center gap-6 px-0 pb-6 collapse-content active">
                <div class="container-full-2 s20 mt-1 mb-5">
                    <!--Options-->
                    <div class="custom-filter-set-area" v-if="showAdvancedFilters">
                        <div class="grid-filter6 mt-4 mb-0">
                            <div class="form-field-floating reactive sm">
                                <input type="text" class="form-input" v-model="filtersParams.name" name="name" id="name" placeholder="" autocomplete="off">
                                <label for="name">Nom, prénoms</label>
                            </div>
                            <div class="form-field-floating sm">
                                <select class="form-input-select" name="num_lot" id="num_lot" v-model="filtersParams.num_lot">
                                    <option value="">Tous les lots</option>
                                    <option :value="option" v-for="(option, index) in startupInfos.lots" :key="'filter_num_lot_' + index">Lot {{ option }}</option>
                                </select>
                                <label for="filters_sort">Lot &lt;=</label>
                            </div>
                            <div class="form-field-floating reactive sm">
                                <input type="date" class="form-input" name="borneInf" id="borneInf" placeholder="" v-model="filtersParams.borneInf">
                                <label for="borneInf">Date assignation &lt;=</label>
                            </div>
                            <div class="form-field-floating reactive sm">
                                <input type="date" class="form-input" name="borneSup" id="borneSup" placeholder="" v-model="filtersParams.borneSup">
                                <label for="borneSup">Date assignation &gt;=</label>
                            </div>
                            <div class="form-field-floating sm">
                                <select class="form-input-select" name="etat_assignation" id="etat_assignation" v-model="filtersParams.etatAssignation">
                                    <option value="">Toutes les cartes</option>
                                    <option value="1">Carte assigné</option>
                                    <option value="2">Aucune carte assigné</option>
                                </select>
                                <label for="etat_assignation">Etat <b class="color-danger">*</b></label>
                            </div>
                        </div>
                    </div>
                    <!---->
                </div>
                <table class="table separator skin3 selection rounded"><!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded)  sm-->
                    <thead>
                        <tr>
                            <th>
                                <label class="btn-circle" for="check-list1">
                                    <input type="checkbox" id="check_all" :checked="areAllItemsSelected" @change="handleCheckAll" />
                                </label>
                            </th>
                            <th></th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Nom &amp; Prénoms</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Numéro</th>
                            <th><img src="/icons/icon-filter-down.svg" class="size-xxs mt--1" alt="" /> N° de Lot</th>
                            <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Banque</th>
                            <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Date de création</th>
                            <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Etat</th>
                            <th class="flex-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="loading">
                            <tr>
                                <td colspan="10" class="text-center py-5">
                                    <div style="display: flex; justify-content: center" v-if="loading">
                                        <div class="loader dark"></div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <template v-else-if="paginatedFilteredDatas.length === 0">
                            <tr>
                                <td colspan="10" class="text-center py-5">
                                    Aucune membre trouvé
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr class="text-grey-700 over-effect over-effect-scale" v-for="(carte, index) of paginatedFilteredDatas" :key="index"><!-- over-effect active-->
                                <td class="selection-item">
                                    <label class="btn-circle" :for="'item_check_' + index">
                                        <input type="checkbox" name="selectedItems[]" :id="'item_check_' + index" :value="carte.user_id" v-model="selectedItems" />
                                    </label>
                                </td>
                                <td>
                                    <!-- <div class="square-lg px-3 py-3" style="background-color: rgb(var(--color-success-rgb), 0.2);">
                                        <img src="/icons/icon-menu-carte.svg" width="100%" />
                                    </div> -->
                                    <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);"><img :src="'/assets/users_avatar/600w/user.jpg'" width="100%" /></div>
                                </td>
                                <td class="font-semibold text-xl">{{ carte.nom_user + ' ' + carte.prenom_user }}</td>
                                <td class="font-semibold text-xl">{{ carte.numero_masque_carte ?? '-' }}</td>
                                <td>{{ carte.num_lot_carte ?? '-' }}</td>
                                <td>{{ carte.nom_banque_carte ?? '-' }}</td>
                                <td>{{ carte.created_at_carte ?? '-' }}</td>
                                <td><span class="tag-state stroke" :class="{'success': carte.etat_assignation, 'warning': !carte.etat_assignation}">{{ carte.etat_assignation ? 'Assigné' : 'Aucune carte assigné' }}</span></td>
                                <td>
                                    <div class="flex-right">
                                        <custom-button type="button" :classesList="'btn-ultra color2'" @click="assignCartes([carte.user_id])" :loading="loadingActions" v-if="!carte.carte_id && collectivite?.verrouille">
                                            <span>Assigner carte</span>
                                        </custom-button>
                                        <template v-else>
                                            &nbsp;
                                        </template>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>

                <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
            </div>
            <div class="clear-both h-25"></div>
        </div>
    </section>
</template>